// The per-user default-model preference's write half (`/v1/me/llm-pref`), split
// out of the `MyKeys` route the way `createKeyRotate` is — the route's job is
// wiring, not owning a third mutation state machine. Lifted when the two-phase
// capture (arc 101 slice 02) pushed that file past the 150-LOC cap.
//
// Both writes share one busy/error pair on purpose: they drive ONE card, so a
// save and a clear cannot be in flight at once. Every write RE-READS rather than
// patching from the echo — the keys pages' posture throughout.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import type { LlmPref } from './my-llm-pref';

export type LlmPrefInput = { readonly provider: string | null; readonly model: string };

export type LlmPrefEditor = {
  readonly pref: () => LlmPref | null | undefined;
  readonly busy: () => boolean;
  readonly error: () => string | null;
  readonly save: (input: LlmPrefInput) => void;
  readonly clear: () => void;
};

export function createLlmPrefEditor(input: {
  /** The read whose `data` the card renders, refetched after every write. */
  readonly query: { readonly data: LlmPref | null | undefined; readonly refetch: () => unknown };
  readonly set: (pref: LlmPrefInput) => Promise<unknown>;
  readonly clear: () => Promise<unknown>;
}): LlmPrefEditor {
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const run = async (write: () => Promise<unknown>, failed: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await write();
    } catch {
      setError(t(failed));
    } finally {
      await input.query.refetch();
      setBusy(false);
    }
  };

  return {
    pref: () => input.query.data,
    busy,
    error,
    save: (pref) => void run(() => input.set(pref), 'my_keys.pref_save_error'),
    clear: () => void run(() => input.clear(), 'my_keys.pref_clear_error'),
  };
}
